import React, { createContext, useCallback, useContext, useState, useEffect } from 'react';
import { api } from '../api/client';
import { useSnackbar } from './SnackbarContext';
import { rememberAuthRedirect } from '../utils/authRedirect';

interface User {
  id: string;
  email: string;
  plan: 'free' | 'pro';
  name?: string;
  created_at?: string;
  updated_at?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, name?: string) => Promise<void>;
  setSession: (user: User, token: string) => void;
  logout: () => void;
  refreshUser: (updated: User) => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const getStoredUser = (): User | null => {
  const storedUser = localStorage.getItem('user');
  if (!storedUser) {
    return null;
  }

  try {
    return JSON.parse(storedUser) as User;
  } catch {
    localStorage.removeItem('user');
    return null;
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => getStoredUser());
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('token'));
  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    api.setToken(token);
  }, [token]);

  const clearSession = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    api.setToken(null);
  }, []);

  const refreshUser = useCallback((updated: User) => {
    setUser(updated);
    localStorage.setItem('user', JSON.stringify(updated));
  }, []);

  // When the backend rejects our stored session token (expired or revoked),
  // drop the client-side session so PrivateRoute sends the user to /login,
  // and remember where they were so login can return them there.
  useEffect(() => {
    api.setOnSessionExpired(() => {
      rememberAuthRedirect(window.location.pathname + window.location.search);
      clearSession();
      showSnackbar('Your session has expired. Please log in again.', { variant: 'info' });
    });
    return () => api.setOnSessionExpired(null);
  }, [clearSession, showSnackbar]);

  const userID = user?.id;

  // Refresh the stored profile whenever the session changes, including once
  // on load. A stale token makes this request 401, which triggers the
  // session-expired handler above before the user interacts with a broken
  // page. Any other failure (network, server down) keeps the cached
  // session: a transient API failure should not log the user out of an
  // otherwise usable browser session.
  useEffect(() => {
    if (!userID || !token) return;

    let canceled = false;
    api.getSelfUser()
      .then((freshUser) => {
        if (!canceled) {
          refreshUser(freshUser);
        }
      })
      .catch(() => {});

    return () => {
      canceled = true;
    };
  }, [token, userID, refreshUser]);

  const setSession = (nextUser: User, nextToken: string) => {
    setUser(nextUser);
    setToken(nextToken);
    localStorage.setItem('user', JSON.stringify(nextUser));
    localStorage.setItem('token', nextToken);
    api.setToken(nextToken);
  };

  const login = async (email: string, password: string) => {
    const response = await api.login(email, password);
    setSession(response.user, response.token);
  };

  const signup = async (email: string, password: string, name?: string) => {
    await api.createUser(email, password, name);
  };

  const logout = clearSession;

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        signup,
        setSession,
        logout,
        refreshUser,
        isAuthenticated: !!user && !!token,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
